import {
  createStylesParams,
  StyleParamType,
  wixFontParam,
  wixColorParam,
} from '@wix/tpa-settings';
import { StyleType } from '@wix/editor-platform-ooi-metadata-resolver';

import {
  IChallengeDisplayStyles,
  IChallengeStyles,
} from './Settings/challengeStyles/challengeStyles.types';
import { challengeStyles as mobileStyles } from './Settings.mobile/challengeStyles';
import { fontValueFix } from '../ChallengesList/stylesParams';
import { CustomCssVarsFn } from '@wix/yoshi-flow-editor';

const challengeDisplayStyles = createStylesParams<IChallengeDisplayStyles>({
  opgDisplayRewards: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ai: { description: 'Whether to show program rewards.' },
    },
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  opgDisplayRewardsForVisitor: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ai: { description: 'Whether to show program rewards to visitors.' },
    },
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  opgDisplayInstructors: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ai: { description: 'Whether to show program instructors.' },
    },
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  opgDisplayGroup: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ai: { description: 'Whether to show the program group.' },
    },
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  opgDisplayHeaderDate: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ai: { description: 'Whether to show the program date in the header.' },
    },
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  opgDisplayHeaderPrice: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ai: { description: 'Whether to show the program price in the header.' },
    },
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  opgDisplayHeaderImage: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ai: { description: 'Whether to show the program image in the header.' },
    },
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  opgDisplayChallengeDuration: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ai: { description: 'Whether to show the program duration.' },
    },
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  opgDisplayChallengeSteps: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ai: { description: 'Whether to show the number of program steps.' },
    },
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  opgDisplayChallengeParticipants: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: false },
      ai: {
        description: 'Whether to show the number of program participants.',
      },
    },
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return false;
    },
  },
  opgDisplayOneApp: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ai: { description: 'Whether to show the Wix app promotion.' },
    },
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  opgDisplayDivider: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ai: { description: 'Whether to show dividers in the program header.' },
    },
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },

  opgDisplayPriceForFreePrograms: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ai: { description: 'Whether to show a price label for free programs.' },
    },
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  opgSocialSharingIsEnabled: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ai: { description: 'Whether to show program sharing controls.' },
    },
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  opgJoinViaOneAppForParticipantIsEnabled: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: false },
      ai: {
        description:
          'Whether participants are prompted to join through the Wix app.',
      },
    },
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return false;
    },
  },
  opgAgendaIsEnabled: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: false },
      ai: { description: 'Whether to show the program agenda.' },
    },
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return false;
    },
  },
  opgAgendaShowTitle: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ai: { description: 'Whether to show the program agenda title.' },
    },
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  opgOverviewTabDisplay: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ai: { description: 'Whether to show the program overview tab.' },
    },
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  /**
   * participant page sidebar layout
   */
  opgSidebarLayoutDisplayChallengeDate: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ai: {
        description: 'Whether to show the program date in the sidebar layout.',
      },
    },
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  opgSidebarLayoutDisplayChallengeDuration: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ai: {
        description:
          'Whether to show the program duration in the sidebar layout.',
      },
    },
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  opgSidebarLayoutDisplayChallengeSteps: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ai: {
        description:
          'Whether to show the number of steps in the sidebar layout.',
      },
    },
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  opgSidebarLayoutDisplayChallengeParticipants: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: false },
      ai: {
        description:
          'Whether to show the participant count in the sidebar layout.',
      },
    },
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return false;
    },
  },
});

export const customCssVars: CustomCssVarsFn = ({ styleParams, isRTL }) => {
  const booleans = styleParams.booleans;
  const strings = styleParams.strings;
  const numbers = styleParams.numbers;
  const cssVars = Object.keys(booleans).reduce((result, key) => {
    if (booleans[key] === false) {
      result[key] = 'none';
    } else {
      result[key] = 'initial';
    }
    return result;
  }, {} as Record<string, string>);
  const hideStepsParticipants =
    booleans.opgDisplayChallengeSteps === false &&
    booleans.opgDisplayChallengeParticipants === false;
  if (hideStepsParticipants) {
    cssVars['opgDisplayChallengeDuration-divider'] = 'none';
  }
  if (hideStepsParticipants && booleans.opgDisplayChallengeDuration === false) {
    cssVars.opgDetailsList = 'none';
  }
  if (cssVars.buttonHoverUnderlineStyle === 'initial') {
    cssVars.buttonHoverUnderlineStyle = 'underline';
  }
  if (cssVars.secondaryButtonHoverUnderlineStyle === 'initial') {
    cssVars.secondaryButtonHoverUnderlineStyle = 'underline';
  }
  if (cssVars.textButtonHoverUnderlineStyle === 'initial') {
    cssVars.textButtonHoverUnderlineStyle = 'underline';
  }
  const opgSidebarLayoutSpace: number =
    styleParams.numbers.opgSidebarLayoutSpace || 0;

  cssVars.opgDisplaySidebarRightBorder =
    opgSidebarLayoutSpace === 0 ? 'none' : 'unset'; // the right border should be hidden if there is no space between section

  cssVars.opgContentTextAlignment =
    numbers.opgContentTextAlignmentAsNumber === 0 ? 'left' : 'right';

  if (cssVars.opgContentTextAlignment === 'left') {
    cssVars.direction = !isRTL ? 'unset' : 'rtl';
    cssVars.rtlScale = !isRTL ? '1' : '-1';
    cssVars.sidebarFlexDirection = !isRTL ? 'row' : 'row-reverse';
    cssVars.sidebarTextAlignment = !isRTL ? 'left' : 'right';
    cssVars.sidebarSummaryMarginLeft = !isRTL ? '0' : 'auto';
    cssVars.sidebarSummaryMarginRight = !isRTL ? 'auto' : '0';
    cssVars.agendaWrapperFlexDirection = 'row'; // dir ="rtl"
    cssVars.agendaTextAlign = 'start'; // dir ="rtl"
    cssVars.sidebarStepsListMarginLeft = !isRTL ? '0' : 'auto';
    cssVars.sidebarQuizBaseAlignment = 'flex-start';
    cssVars.sidebarControlsJustifyContent = !isRTL ? 'flex-start' : 'flex-end';
    cssVars.sidebarControlsJustifyContentNewDesign = 'flex-end';
    cssVars.sidebarStepsNavigationMarginLeft = !isRTL ? 'auto' : '0';
    cssVars.sidebarStepsNavigationMarginRight = !isRTL ? '0' : 'auto';
    cssVars.sidebarStatsJustifyContent = !isRTL ? 'flex-start' : 'flex-end';
  } else {
    cssVars.direction = !isRTL ? 'rtl' : 'unset';
    cssVars.rtlScale = !isRTL ? '-1' : '1';
    cssVars.sidebarFlexDirection = !isRTL ? 'row-reverse' : 'row';
    cssVars.sidebarTextAlignment = !isRTL ? 'right' : 'left';
    cssVars.sidebarSummaryMarginLeft = !isRTL ? 'auto' : '0';
    cssVars.sidebarSummaryMarginRight = !isRTL ? '0' : 'auto';
    cssVars.agendaWrapperFlexDirection = 'row-reverse'; // dir ="rtl"
    cssVars.agendaTextAlign = 'end'; // dir ="rtl"
    cssVars.sidebarStepsListMarginLeft = !isRTL ? 'auto' : '0';
    cssVars.sidebarQuizBaseAlignment = 'flex-end';
    cssVars.sidebarControlsJustifyContent = !isRTL ? 'flex-end' : 'flex-start';
    cssVars.sidebarControlsJustifyContentNewDesign = 'flex-start';
    cssVars.sidebarStepsNavigationMarginLeft = !isRTL ? '0' : 'auto';
    cssVars.sidebarStepsNavigationMarginRight = !isRTL ? 'auto' : '0';
    cssVars.sidebarStatsJustifyContent = !isRTL ? 'flex-end' : 'flex-start';
  }

  return cssVars;
};

const stylesParams = createStylesParams<IChallengeStyles>({
  visitorPageBodySidebarProportions: {
    metadata: {
      type: StyleType.width,
      defaultValue: { type: 'static', value: 40 },
      ai: {
        description:
          'Relative width of the sidebar on the participant program page.',
      },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 40,
  },
  visitorPageBodySidebarVerticalPadding: {
    metadata: {
      type: StyleType.padding,
      defaultValue: { type: 'static', value: 60 },
      ai: {
        description:
          'Vertical padding inside the participant program page sidebar.',
      },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 60,
  },
  visitorPageBodySidebarHorizontalPadding: {
    metadata: {
      type: StyleType.padding,
      defaultValue: { type: 'static', value: 60 },
      ai: {
        description:
          'Horizontal padding inside the participant program page sidebar.',
      },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 60,
  },
  visitorHeaderPartsSpacing: {
    metadata: {
      type: StyleType.gap,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Space between items in the participant program page header.',
      },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  visitorPageBodySectionsSpacing: {
    metadata: {
      type: StyleType.gap,
      defaultValue: { type: 'static', value: 60 },
      mobileDefaultValue: { type: 'static', value: 20 },
      ai: {
        description:
          'Vertical space between sections in the participant program page body.',
      },
    },
    inheritDesktop: false,
    type: StyleParamType.Number,
    getDefaultValue: ({ isMobile }) => (isMobile ? 20 : 60),
  },
  visitorPageBodySidebarGap: {
    metadata: {
      type: StyleType.gap,
      defaultValue: { type: 'static', value: 20 },
      ai: {
        description:
          'Space between the participant program page body and sidebar.',
      },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 20,
  },
  visitorPageVerticalPadding: {
    metadata: {
      type: StyleType.padding,
      defaultValue: { type: 'static', value: 80 },
      mobileDefaultValue: { type: 'static', value: 20 },
      ai: {
        description:
          'Vertical padding around the participant program page header.',
      },
    },
    inheritDesktop: false,
    type: StyleParamType.Number,
    getDefaultValue: ({ isMobile }) => (isMobile ? 20 : 80),
  },
  visitorPageHorizontalPadding: {
    metadata: {
      type: StyleType.padding,
      defaultValue: { type: 'static', value: 0 },
      mobileDefaultValue: { type: 'static', value: 20 },
      ai: {
        description:
          'Horizontal padding around the participant program page header.',
      },
    },
    inheritDesktop: false,
    type: StyleParamType.Number,
    getDefaultValue: ({ isMobile }) => (isMobile ? 20 : 0),
  },
  visitorPageBodyVerticalPadding: {
    metadata: {
      type: StyleType.padding,
      defaultValue: { type: 'static', value: 60 },
      mobileDefaultValue: { type: 'static', value: 20 },
      ai: {
        description:
          'Vertical padding inside the participant program page body.',
      },
    },
    inheritDesktop: false,
    type: StyleParamType.Number,
    getDefaultValue: ({ isMobile }) => (isMobile ? 20 : 60),
  },
  visitorPageBodyHorizontalPadding: {
    metadata: {
      type: StyleType.padding,
      defaultValue: { type: 'static', value: 60 },
      mobileDefaultValue: { type: 'static', value: 20 },
      ai: {
        description:
          'Horizontal padding inside the participant program page body.',
      },
    },
    inheritDesktop: false,
    type: StyleParamType.Number,
    getDefaultValue: ({ isMobile }) => (isMobile ? 20 : 60),
  },
  /**
   * sidebar styles
   */
  sidebarLayoutProgressBarProgressColor: {
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'color-8' },
      ai: {
        description:
          'Color of the completed portion of the program progress bar.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  sidebarLayoutSelectedStepColor: {
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'color-8', opacity: 0.1 },
      ai: {
        description:
          'Background color of the selected step in the program sidebar.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.1),
  },
  sidebarLayoutOverviewDescriptionFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: { description: 'Font used for the program overview description.' },
    },
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Body-M', {
          size: 16,
        }),
        textPresets,
      );
    },
  },
  sidebarLayoutOverviewDescriptionColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5' },
      ai: { description: 'Text color of the program overview description.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  sidebarLayoutOverviewTitleFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Page-title', size: 28 },
      ai: { description: 'Font used for the program overview title.' },
    },
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Page-title', {
          size: 28,
        }),
        textPresets,
      );
    },
  },
  sidebarLayoutOverviewTitleColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5' },
      ai: { description: 'Text color of the program overview title.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  sidebarLayoutBodyDescriptionFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: {
        description: 'Font used for step descriptions in the program body.',
      },
    },
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Body-M', {
          size: 16,
        }),
        textPresets,
      );
    },
  },
  sidebarLayoutBodyDescriptionColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5' },
      ai: {
        description: 'Text color of step descriptions in the program body.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  sidebarLayoutBodyStepNameFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Page-title', size: 28 },
      mobileDefaultValue: { type: 'static', preset: 'Page-title', size: 16 },
      ai: { description: 'Font used for step names in the program body.' },
    },
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets, isMobile }) => {
      return fontValueFix(
        wixFontParam('Page-title', {
          size: isMobile ? 16 : 28,
        }),
        textPresets,
      );
    },
  },
  sidebarLayoutBodyStepNameColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5' },
      ai: { description: 'Text color of step names in the program body.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  sidebarLayoutSidebarStepNameFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: { description: 'Font used for step names in the program sidebar.' },
    },
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Body-M', {
          size: 16,
        }),
        textPresets,
      );
    },
  },
  sidebarLayoutSidebarStepNameColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5' },
      ai: { description: 'Text color of step names in the program sidebar.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  sidebarLayoutSidebarSectionNameFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Page-title', size: 16 },
      ai: {
        description: 'Font used for section names in the program sidebar.',
      },
    },
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Page-title', {
          size: 16,
        }),
        textPresets,
      );
    },
  },
  sidebarLayoutSidebarSectionNameColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5' },
      ai: {
        description: 'Text color of section names in the program sidebar.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  sidebarLayoutSidebarChallengeNameFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: { description: 'Font used for the program name in the sidebar.' },
    },
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Body-M', {
          size: 16,
        }),
        textPresets,
      );
    },
  },
  sidebarLayoutSidebarChallengeNameColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5' },
      ai: { description: 'Text color of the program name in the sidebar.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  sidebarLayoutStepDividerWidth: {
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: { type: 'static', value: 1 },
      ai: {
        description: 'Width of dividers between steps in the program sidebar.',
      },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  sidebarLayoutDividerOpacityAndColor: {
    metadata: {
      type: StyleType.borderColor,
      defaultValue: { type: 'static', preset: 'color-4' },
      ai: {
        description:
          'Color and opacity of dividers in the participant sidebar.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-4'),
  },
  sidebarLayoutBodyBackgroundColor: {
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'color-1' },
      ai: {
        description:
          'Background color of the participant program body in sidebar layout.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  sidebarLayoutSidebarBackgroundColor: {
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'color-1' },
      ai: {
        description:
          'Background color of the participant program sidebar in sidebar layout.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  sidebarLayoutButtonFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: { description: 'Font used for participant program action buttons.' },
    },
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Body-M', {
          size: 16,
        }),
        textPresets,
      );
    },
  },
  sidebarLayoutButtonColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-1' },
      ai: { description: 'Text color of participant program action buttons.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  sidebarLayoutButtonBackground: {
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'color-8' },
      ai: {
        description: 'Background color of participant program action buttons.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  sidebarLayoutButtonBorderBackground: {
    metadata: {
      type: StyleType.borderColor,
      defaultValue: { type: 'static', preset: 'color-8' },
      ai: {
        description: 'Border color of participant program action buttons.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  sidebarLayoutButtonBorderWidth: {
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: { type: 'static', value: 1 },
      ai: {
        description: 'Border width of participant program action buttons.',
      },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  sidebarLayoutButtonBorderRadius: {
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description: 'Corner radius of participant program action buttons.',
      },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  sidebarLayoutButtonHoverBackground: {
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'color-8', opacity: 0.7 },
      ai: {
        description:
          'Background color of participant program action buttons when hovered.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.7),
  },
  sidebarLayoutButtonHoverBorderBackground: {
    metadata: {
      type: StyleType.borderColor,
      defaultValue: { type: 'static', preset: 'color-8', opacity: 0.7 },
      ai: {
        description:
          'Border color of participant program action buttons when hovered.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.7),
  },
  sidebarLayoutButtonHoverFontColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-1' },
      ai: {
        description:
          'Text color of participant program action buttons when hovered.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  sidebarLayoutSecondaryButtonColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-8' },
      ai: { description: 'Text color of secondary action buttons.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  sidebarLayoutSecondaryButtonBorderBackground: {
    metadata: {
      type: StyleType.borderColor,
      defaultValue: { type: 'static', preset: 'color-8' },
      ai: { description: 'Border color of secondary action buttons.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  sidebarLayoutSecondaryButtonBorderWidth: {
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: { type: 'static', value: 1 },
      ai: { description: 'Border width of secondary action buttons.' },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  sidebarLayoutSecondaryButtonBorderRadius: {
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: { description: 'Corner radius of secondary action buttons.' },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  sidebarLayoutSecondaryButtonHoverBorderBackground: {
    metadata: {
      type: StyleType.borderColor,
      defaultValue: { type: 'static', preset: 'color-8', opacity: 0.7 },
      ai: {
        description: 'Border color of secondary action buttons when hovered.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.7),
  },
  sidebarLayoutSecondaryButtonHoverFontColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-8' },
      ai: {
        description: 'Text color of secondary action buttons when hovered.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  sidebarLayoutTextButtonColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-8' },
      ai: { description: 'Text color of text-only action buttons.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  sidebarLayoutTextButtonHoverFontColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-8' },
      ai: {
        description: 'Text color of text-only action buttons when hovered.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  sidebarLayoutBodySidePaddingFullWidth: {
    metadata: {
      type: StyleType.padding,
      defaultValue: { type: 'static', value: 80 },
      ai: { description: 'Horizontal padding of the full-width program body.' },
    },
    inheritDesktop: false,
    type: StyleParamType.Number,
    getDefaultValue() {
      return 80;
    },
  },
  sidebarLayoutBodySidePadding: {
    metadata: {
      type: StyleType.padding,
      defaultValue: { type: 'static', value: 32 },
      ai: {
        description:
          'Horizontal padding of the program body in the sidebar layout.',
      },
    },
    inheritDesktop: false,
    type: StyleParamType.Number,
    getDefaultValue() {
      return 32;
    },
  },
  sidebarLayoutSidebarBodySpaceFromTop: {
    metadata: {
      type: StyleType.paddingTop,
      defaultValue: { type: 'static', value: 32 },
      ai: { description: 'Space above the step list in the program sidebar.' },
    },
    inheritDesktop: false,
    type: StyleParamType.Number,
    getDefaultValue() {
      return 32;
    },
  },
  sidebarLayoutSidebarSpaceBetweenSteps: {
    metadata: {
      type: StyleType.padding,
      defaultValue: { type: 'static', value: 20 },
      ai: { description: 'Space between steps in the program sidebar.' },
    },
    inheritDesktop: false,
    type: StyleParamType.Number,
    getDefaultValue() {
      return 20;
    },
  },
  /**
   * sidebar styles
   */
  sidebarLayoutSidebarSidePadding: {
    metadata: {
      type: StyleType.padding,
      defaultValue: { type: 'static', value: 20 },
      ai: { description: 'Horizontal padding inside the program sidebar.' },
    },
    inheritDesktop: false,
    type: StyleParamType.Number,
    getDefaultValue() {
      return 20;
    },
  },
  sidebarLayoutSpace: {
    metadata: {
      type: StyleType.gap,
      defaultValue: { type: 'static', value: 0 },
      ai: { description: 'Space between the program body and sidebar.' },
    },
    inheritDesktop: false,
    type: StyleParamType.Number,
    getDefaultValue() {
      return 0;
    },
  },
  opgSidebarLayoutSpace: {
    metadata: {
      type: StyleType.gap,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description: 'Space separating the program body from the sidebar.',
      },
    },
    inheritDesktop: false,
    type: StyleParamType.Number,
    getDefaultValue() {
      return 0;
    },
  },
  sidebarLayoutProportionsFullWidth: {
    metadata: {
      type: StyleType.width,
      defaultValue: { type: 'static', value: 30 },
      ai: { description: 'Relative width of the full-width program sidebar.' },
    },
    inheritDesktop: false,
    type: StyleParamType.Number,
    getDefaultValue() {
      return 30;
    },
  },
  sidebarLayoutProportions: {
    metadata: {
      type: StyleType.width,
      defaultValue: { type: 'static', value: 30 },
      ai: { description: 'Relative width of the program sidebar.' },
    },
    inheritDesktop: false,
    type: StyleParamType.Number,
    getDefaultValue() {
      return 30;
    },
  },
  sidebarLayoutVideoOverlayColor: {
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'black/white', opacity: 0.2 },
      ai: {
        description:
          'Color of the video overlay in the participant sidebar layout.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('black/white', 0.2),
  },
  sidebarLayoutVideoPlayButtonColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'white/black', opacity: 1 },
      ai: {
        description:
          'Color of video play buttons in the participant sidebar layout.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('white/black', 1),
  },
  /**
   * visitor styles
   */
  headerBackgroundColor: {
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'color-8', opacity: 0.1 },
      ai: {
        description: 'Background color of the participant program page header.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.1),
  },
  pageBackgroundColor: {
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'color-1' },
      ai: {
        description:
          'Background color of the participant program body in classic layout.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  challengeNameFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Page-title', size: 40 },
      ai: { description: 'Font used for the participant program name.' },
    },
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Page-title', {
          size: 40,
        }),
        textPresets,
      );
    },
  },
  challengeNameColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5' },
      ai: { description: 'Text color of the participant program name.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  dateFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: { description: 'Font used for the program date.' },
    },
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Body-M', {
          size: 16,
        }),
        textPresets,
      );
    },
  },
  dateColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5' },
      ai: { description: 'Text color of the program date.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  priceFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: { description: 'Font used for the program price.' },
    },
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Body-M', {
          size: 16,
        }),
        textPresets,
      );
    },
  },
  priceColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5' },
      ai: { description: 'Text color of the program price.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  sectionTitleFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Page-title', size: 32 },
      ai: { description: 'Font used for program page section titles.' },
    },
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Page-title', {
          size: 32,
        }),
        textPresets,
      );
    },
  },
  sectionTitleColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5' },
      ai: { description: 'Text color of participant page section titles.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  countersFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Page-title', size: 20 },
      ai: { description: 'Font used for program statistic values.' },
    },
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Page-title', {
          size: 20,
        }),
        textPresets,
      );
    },
  },
  countersColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5' },
      ai: { description: 'Text color of program statistic values.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  countersDescriptionFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: { description: 'Font used for program statistic labels.' },
    },
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Body-M', {
          size: 16,
        }),
        textPresets,
      );
    },
  },
  countersDescriptionColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5' },
      ai: { description: 'Text color of program statistic labels.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  descriptionFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: { description: 'Font used for participant program descriptions.' },
    },
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Body-M', {
          size: 16,
        }),
        textPresets,
      );
    },
  },
  descriptionColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5' },
      ai: { description: 'Text color of participant program descriptions.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  buttonFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 18 },
      ai: {
        description:
          'Font used for participant program primary action buttons.',
      },
    },
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Body-M', {
          size: 18,
        }),
        textPresets,
      );
    },
  },
  buttonColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-1' },
      ai: {
        description:
          'Text color of participant program primary action buttons.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  buttonBackground: {
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'color-8' },
      ai: {
        description:
          'Background color of participant program primary action buttons.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  buttonBorderBackground: {
    metadata: {
      type: StyleType.borderColor,
      defaultValue: { type: 'static', preset: 'color-8' },
      ai: {
        description:
          'Border color of participant program primary action buttons.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  buttonBorderWidth: {
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: { type: 'static', value: 1 },
      ai: {
        description:
          'Border width of participant program primary action buttons.',
      },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  buttonBorderRadius: {
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Corner radius of participant program primary action buttons.',
      },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  buttonHoverBackground: {
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'color-8', opacity: 0.7 },
      ai: {
        description:
          'Background color of participant program primary action buttons when hovered.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.7),
  },
  buttonHoverBorderBackground: {
    metadata: {
      type: StyleType.borderColor,
      defaultValue: { type: 'static', preset: 'color-8', opacity: 0.7 },
      ai: {
        description:
          'Border color of participant program primary action buttons when hovered.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.7),
  },
  buttonHoverFontColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-1' },
      ai: {
        description:
          'Text color of participant program primary action buttons when hovered.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  buttonHoverUnderlineStyle: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: false },
      ai: {
        description:
          'Whether primary action button text is underlined when hovered.',
      },
    },
    type: StyleParamType.Boolean,
    getDefaultValue: ({ experiments }) => {
      return (
        !!experiments?.enabled('specs.programs.ButtonsUnderlineStyle') || false
      );
    },
  },
  secondaryButtonHoverUnderlineStyle: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: false },
      ai: {
        description:
          'Whether secondary button text is underlined when hovered.',
      },
    },
    type: StyleParamType.Boolean,
    getDefaultValue: ({ experiments }) => {
      return (
        !!experiments?.enabled('specs.programs.ButtonsUnderlineStyle') || false
      );
    },
  },
  textButtonHoverUnderlineStyle: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: false },
      ai: {
        description:
          'Whether text-only button text is underlined when hovered.',
      },
    },
    type: StyleParamType.Boolean,
    getDefaultValue: ({ experiments }) => {
      return (
        !!experiments?.enabled('specs.programs.ButtonsUnderlineStyle') || false
      );
    },
  },
  dividerColor: {
    metadata: {
      type: StyleType.borderColor,
      defaultValue: { type: 'static', preset: 'color-3' },
      ai: {
        description:
          'Color of dividers in the participant program page header.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-3'),
  },
  dividerLength: {
    metadata: {
      type: StyleType.number,
      defaultValue: { type: 'static', value: 100 },
      ai: {
        description:
          'Length of dividers in the participant program page header.',
      },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 100,
  },
  dividerWidth: {
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: { type: 'static', value: 1 },
      ai: {
        description:
          'Width of dividers in the participant program page header.',
      },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  linksColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-8' },
      ai: {
        description: 'Text color of links on the participant program page.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  mediaSectionHeight: {
    metadata: {
      type: StyleType.height,
      defaultValue: { type: 'static', value: 500 },
      ai: {
        description: 'Height of the participant program page media section.',
      },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 500,
  },
  contentDividersColor: {
    metadata: {
      type: StyleType.borderColor,
      defaultValue: { type: 'static', preset: 'color-5', opacity: 0.2 },
      ai: {
        description:
          'Color of dividers between participant program page sections.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.2),
  },
  contentDividersWidth: {
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: { type: 'static', value: 1 },
      ai: {
        description:
          'Width of dividers between participant program page sections.',
      },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  agendaBoxPadding: {
    metadata: {
      type: StyleType.padding,
      defaultValue: { type: 'static', value: 20 },
      ai: { description: 'Padding inside the program agenda.' },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 20,
  },
  agendaBackground: {
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'color-1' },
      ai: { description: 'Background color of the program agenda.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  agendaBorderColor: {
    metadata: {
      type: StyleType.borderColor,
      defaultValue: { type: 'static', preset: 'color-5' },
      ai: { description: 'Border color of the participant program agenda.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  agendaBorderWidth: {
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: { type: 'static', value: 1 },
      ai: { description: 'Border width of the program agenda.' },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  agendaDividersColor: {
    metadata: {
      type: StyleType.borderColor,
      defaultValue: { type: 'static', preset: 'color-5' },
      ai: {
        description: 'Color of dividers in the participant program agenda.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  agendaDividersWidth: {
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: { type: 'static', value: 1 },
      ai: { description: 'Width of dividers in the program agenda.' },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  agendaIconColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5' },
      ai: { description: 'Color of icons in the program agenda.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  agendaSectionTitleFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: {
        description: 'Font used for section titles in the program agenda.',
      },
    },
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Body-M', {
          size: 16,
        }),
        textPresets,
      );
    },
  },
  agendaSectionTitleColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5' },
      ai: {
        description: 'Text color of section titles in the program agenda.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  agendaStepNameFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: { description: 'Font used for step names in the program agenda.' },
    },
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Body-M', {
          size: 16,
        }),
        textPresets,
      );
    },
  },
  agendaStepNameColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5' },
      ai: { description: 'Text color of step names in the program agenda.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  agendaStepCountFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 14 },
      ai: { description: 'Font used for step counts in the program agenda.' },
    },
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Body-M', {
          size: 14,
        }),
        textPresets,
      );
    },
  },
  agendaStepCountColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5' },
      ai: { description: 'Text color of step counts in the program agenda.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  agendaLinkFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: { description: 'Font used for links in the program agenda.' },
    },
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Body-M', {
          size: 16,
        }),
        textPresets,
      );
    },
  },
  agendaLinkColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5' },
      ai: { description: 'Text color of links in the program agenda.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  sidebarBackgroundColor: {
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'color-8', opacity: 0.1 },
      ai: {
        description:
          'Background color of the participant program sidebar in classic layout.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.1),
  },
  sidebarDividersColor: {
    metadata: {
      type: StyleType.borderColor,
      defaultValue: { type: 'static', preset: 'color-3' },
      ai: {
        description:
          'Color of dividers in the participant program page sidebar.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-3'),
  },
  sidebarDividersWidth: {
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: { type: 'static', value: 1 },
      ai: {
        description:
          'Width of dividers in the participant program page sidebar.',
      },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  sidebarSectionTitleFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Page-title', size: 32 },
      ai: {
        description:
          'Font used for section titles in the program page sidebar.',
      },
    },
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Page-title', {
          size: 32,
        }),
        textPresets,
      );
    },
  },
  sidebarSectionTitleColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5' },
      ai: {
        description:
          'Text color of section titles in the participant page sidebar.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  videoOverlayColor: {
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'black/white', opacity: 0.2 },
      ai: {
        description:
          'Color of the overlay shown on participant program videos.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('black/white', 0.2),
  },
  videoPlayButtonColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'white/black', opacity: 1 },
      ai: {
        description:
          'Color of the play button shown on participant program videos.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('white/black', 1),
  },
  opgContentTextAlignment: {
    type: StyleParamType.String,
    getDefaultValue({ isRTL }) {
      return isRTL ? 'right' : 'left';
    },
  },
  opgContentTextAlignmentAsNumber: {
    metadata: {
      type: StyleType.numberEnum,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Horizontal alignment of content on the participant program page: left (0) or right (1).',
      },
    },
    type: StyleParamType.Number,
    getDefaultValue: ({ isRTL }) => {
      return isRTL ? 1 : 0;
    },
  },
  ...challengeDisplayStyles,
  ...mobileStyles,
});

export default stylesParams;

export type IChallengeStylesKeys = keyof IChallengeStyles;
export type IChallengeStylesVars = `--${IChallengeStylesKeys}`;
